import React, { useState } from "react";
import { updatePassword } from "aws-amplify/auth";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const passwordRules = [
  { label: "At least 8 characters", test: (pw: string) => pw.length >= 8 },
  { label: "At least 1 uppercase letter", test: (pw: string) => /[A-Z]/.test(pw) },
  { label: "At least 1 lowercase letter", test: (pw: string) => /[a-z]/.test(pw) },
  { label: "At least 1 number", test: (pw: string) => /\d/.test(pw) },
];

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Visibility toggles
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const allValid = passwordRules.every((rule) => rule.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword;

  const handleChangePassword = async () => {
    setLoading(true);
    setErrorMessage("");

    if (!allValid) {
      setErrorMessage("New password does not meet requirements.");
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      await updatePassword({ oldPassword, newPassword });
      alert("Password changed successfully!");
      onClose();
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to change password");
      console.error("Change Password Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        <TextField
          label="Current Password"
          type={showOld ? "text" : "password"}
          fullWidth
          margin="normal"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOld((prev) => !prev)} edge="end">
                  {showOld ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="New Password"
          type={showNew ? "text" : "password"}
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNew((prev) => !prev)} edge="end">
                  {showNew ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm New Password"
          type={showConfirm ? "text" : "password"}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={!!confirmPassword && !passwordsMatch}
          helperText={confirmPassword && !passwordsMatch ? "Passwords do not match" : ""}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <List dense>
          {passwordRules.map((rule, i) => (
            <ListItem key={i}>
              <ListItemText
                primary={
                  <Typography color={rule.test(newPassword) ? "green" : "red"}>
                    {rule.label}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} color="error">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleChangePassword}
          color="error"
          disabled={loading || !allValid || !passwordsMatch}
        >
          {loading ? "Changing..." : "Change Password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
